import { useQuery } from '@tanstack/react-query';
import { fetcher } from '@/shared/api';
import { storeKeys } from './queryKeys';
import type { ListQueryParams } from '@/shared/types/list';
import { toQueryString } from '@/shared/utils/toQueryString';
import type { SystemStoreDetail, StoreTimezoneHistoryRow } from '../model/types';
import { unwrapStore } from '../model/unwrapStore';

/**
 * `status` is `ACTIVE | SUSPENDED` — read-only on the list; the only writers are
 * the explicit suspend/reactivate verbs.
 *
 * The four below are the design review's requested filters and window, and they
 * are **not shipped**: the endpoint takes `paginate/search/page/limit/sort/dir`
 * plus `status` (PR #423) and nothing else. They are typed here so the page has
 * one place to name them, and the page only ever SENDS them once the response
 * proves the change landed — see `storeListMetrics.ts`. An unknown parameter is
 * silently dropped by this API, so sending one speculatively would render an
 * unfiltered list under a filter control.
 */
export type SystemStoreListParams = ListQueryParams & {
  status?: string;
  /** `Plan.code` — FREE_TRIAL | STARTER | GROWTH. */
  planCode?: string;
  /** `StoreSubscription.status` — one of the six. */
  subscriptionStatus?: string;
  /**
   * Province, not city: city is free text and yields three spellings of Kathmandu.
   *
   * 🔴 **Do not build a filter control on this.** Province is free text too — the field
   * just fails less loudly. Matched against Nepal's seven provinces, 5 of 235 stores
   * resolve; the rest hold city names, foreign states, a phone number and lorem ipsum.
   * The 2026-09-01 handoff cut the geography chart for exactly this reason and warns
   * against the filter by name: a picker of the seven provinces would return almost
   * nothing for six of them and read as "we have no merchants there".
   *
   * No caller passes it today, and that is deliberate rather than unfinished. If a
   * location filter is wanted, it needs a validated column first.
   */
  province?: string;
  /** The window the commercial metrics are summed over. */
  range?: string;
};

export function useSystemStoreList(params: SystemStoreListParams = {}) {
  return useQuery({
    queryKey: storeKeys.systemList(params as Record<string, unknown>),
    queryFn: () => fetcher(`/stores${toQueryString(params)}`),
    // Hold the current page on screen while the next one loads, instead of
    // flashing an empty table between pages.
    keepPreviousData: true,
  });
}

export function useSiteSettingDetails(id: string | undefined) {
  return useQuery({
    queryKey: storeKeys.settings(id ?? ''),
    queryFn: () => fetcher(`/stores/${id}`),
    enabled: !!id,
  });
}

/**
 * One store, for the admin detail page.
 *
 * NOT `useSiteSettingDetails` — that hook calls the same `/stores/${id}` path but
 * keys on `["store-settings", id]` and has nine merchant consumers. Since the
 * namespace interceptor derives the prefix from the caller's claims, sharing the
 * key would let a system-namespace response serve merchant screens from cache.
 *
 * The response envelope is unwrapped through the shared `unwrapStore` because the
 * docs describe the 200 in prose with no schema, so it may arrive wrapped or bare
 * — and `useSystemStoreUpdate` writes the PATCH response to this same key, so
 * both paths must agree on the envelope or a save would blank the page.
 */
export function useSystemStoreDetail(storeId?: string) {
  return useQuery<SystemStoreDetail>({
    queryKey: storeKeys.systemDetail(storeId ?? ''),
    queryFn: async () => unwrapStore(await fetcher<unknown>(`/stores/${storeId}`)) ?? {},
    enabled: !!storeId,
    staleTime: 60 * 1000,
  });
}

/**
 * A store's timezone change history, newest first (the server caps at 20).
 *
 * `enabled` is a caller decision, not a default: the history lives behind a
 * closed disclosure, and most stores will never change their timezone, so this
 * must not cost a request on every settings page load.
 */
export function useStoreTimezoneHistory(storeId: string | undefined, enabled: boolean) {
  return useQuery<StoreTimezoneHistoryRow[]>({
    queryKey: storeKeys.timezoneHistory(storeId ?? ''),
    queryFn: async () => {
      const res = await fetcher<unknown>(`/stores/${storeId}/timezone/history`);
      if (Array.isArray(res)) return res as StoreTimezoneHistoryRow[];
      const rows = (res as { data?: unknown })?.data;
      return Array.isArray(rows) ? (rows as StoreTimezoneHistoryRow[]) : [];
    },
    enabled: !!storeId && enabled,
  });
}
